import React, { useState } from "react";
import {
  Paper,
  Text,
  Image,
  Box,
  Button,
  Stack,
  Group,
  Container,
  TextInput,
  Select,
  ThemeIcon,
  Divider,
  Notification,
} from "@mantine/core";
import { IconUser, IconShieldCheck, IconCheck, IconX } from "@tabler/icons-react";
import { useGlobal } from "../../context/global-context";
import { apiFetch, describeApiError } from "../../utils/api";
import { runStatusCheck } from "../../utils/status-check";
import { ConsentCheckbox, ConsentIndicator } from "../../components/consent-checkbox";
import { consentBodyFields, isSiteConsent } from "../../utils/consent";
import VerificationIframe from "../../components/verification-iframe";
import FinTechOne from "../../assets/fintech_one.png";

const US_STATES = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" }, { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" }, { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" }, { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" }, { value: "DC", label: "District of Columbia" },
];

export default function Home() {
  const { globalState, wineStoreSettings } = useGlobal();
  const [consentAcceptedAt, setConsentAcceptedAt] = useState(null);
  const consentRequired = isSiteConsent(wineStoreSettings) && !consentAcceptedAt;

  const optionalFields = wineStoreSettings?.optionalFields || {};

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [error, setError] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [verified, setVerified] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [failureCount, setFailureCount] = useState(0);
  const [verificationIframeOpened, setVerificationIframeOpened] = useState(false);
  const [verificationLink, setVerificationLink] = useState("");
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    dobMonth: "",
    dobDay: "",
    dobYear: "",
    ssn4: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const formatPhone = (value) => {
    let digits = value.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
    digits = digits.slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleFormChange = (field, value) => {
    const newValue = field === "phone" ? formatPhone(value) : value;
    setFormData((prev) => ({ ...prev, [field]: newValue }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const isFormComplete =
    formData.email &&
    /\S+@\S+\.\S+/.test(formData.email) &&
    (!optionalFields.firstName || formData.firstName) &&
    (!optionalFields.lastName || formData.lastName) &&
    (!optionalFields.phone || formData.phone) &&
    (!optionalFields.dob || (formData.dobMonth && formData.dobDay && formData.dobYear)) &&
    (!optionalFields.ssn4 || (formData.ssn4 && formData.ssn4.length === 4)) &&
    (!optionalFields.address || (formData.address && formData.city && formData.state && formData.zipCode));

  const handleOpenAccount = async () => {
    setFormErrors({});
    setApiError(null);

    const errors = {};
    if (!formData.email) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = "Invalid email";
    if (optionalFields.firstName && !formData.firstName) errors.firstName = "First name is required";
    if (optionalFields.lastName && !formData.lastName) errors.lastName = "Last name is required";
    if (optionalFields.phone && !formData.phone) errors.phone = "Phone number is required";
    if (optionalFields.dob && (!formData.dobMonth || !formData.dobDay || !formData.dobYear)) errors.dob = "Date of birth is required";
    if (optionalFields.ssn4 && (!formData.ssn4 || formData.ssn4.length !== 4)) errors.ssn4 = "Last 4 of SSN is required";
    if (optionalFields.address) {
      if (!formData.address) errors.address = "Address is required";
      if (!formData.city) errors.city = "City is required";
      if (!formData.state) errors.state = "State is required";
      if (!formData.zipCode) errors.zipCode = "ZIP code is required";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    setLoadingMessage("Getting verification session...");

    try {
      const data = await apiFetch("/demo/vx/create-journey.php", {
            email: formData.email,
            ...(optionalFields.phone && formData.phone ? { phone: formData.phone.replace(/\D/g, "") } : {}),
            target: {
              ...(optionalFields.firstName && formData.firstName ? { fn: formData.firstName } : {}),
              ...(optionalFields.lastName && formData.lastName ? { ln: formData.lastName } : {}),
              ...(optionalFields.phone && formData.phone ? { phone: formData.phone.replace(/\D/g, "") } : {}),
              ...(optionalFields.dob && formData.dobYear ? { dob: `${formData.dobYear}${formData.dobMonth.padStart(2, "0")}${formData.dobDay.padStart(2, "0")}` } : {}),
              ...(optionalFields.ssn4 && formData.ssn4 ? { ssn4: formData.ssn4 } : {}),
              ...(optionalFields.address && formData.address ? { addr: formData.address, city: formData.city, state: formData.state, zip: formData.zipCode } : {}),
            },
            demo: "fintech",
            age: `${wineStoreSettings?.age ?? 21}+`,
            method: wineStoreSettings?.integrationMethod || "iframe",
            ...consentBodyFields(wineStoreSettings, consentAcceptedAt),
      });

      if (data.link) {
        if ((wineStoreSettings?.integrationMethod || "iframe") === "redirect") {
          window.open(data.link, "_blank");
          return;
        }

        setVerificationLink(data.link);
        setVerificationIframeOpened(true);
      } else {
        throw new Error("No verification link received");
      }
    } catch (err) {
      console.error("Verification error:", err);
      setApiError(describeApiError(err));
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  // Handle redirect return - check for request_id in query params
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestId = params.get("request_id");
    if (!requestId) return;

    const checkResult = async () => {
      setLoading(true);
      setLoadingMessage("Checking verification result...");
      setApiError(null);

      try {
        const data = await apiFetch("/demo/vx/get-result.php", {
          request_id: requestId,
          demo: "fintech",
        });

        if (data.status === "PASS") {
          setVerified(true);
          setVerificationFailed(false);
        } else {
          setVerificationFailed(true);
          setVerified(false);
          setFailureCount((prev) => prev + 1);
        }
      } catch (error) {
        console.error("Get result error:", error);
        setApiError(describeApiError(error));
      } finally {
        setLoading(false);
        setLoadingMessage("");
        window.history.replaceState({}, "", window.location.pathname);
      }
    };

    checkResult();
  }, []); // Run once on mount

  // Listen for messages from the iframe
  React.useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === "CLOSE") {
        runStatusCheck("fintech");
        setVerificationIframeOpened(false);
      }
      if (event.data === "PASS") {
        runStatusCheck("fintech");
        setVerificationIframeOpened(false);
        setVerified(true);
        setVerificationFailed(false);
      }
      if (event.data === "FAIL") {
        runStatusCheck("fintech");
        setVerificationIframeOpened(false);
        setVerificationFailed(true);
        setFailureCount((prev) => prev + 1);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <>
      {/* Keep input font-size at 16px+ so mobile browsers don't zoom on focus */}
      <style>{`
        .vt-no-zoom input,
        .vt-no-zoom textarea,
        .vt-no-zoom select {
          font-size: 16px;
        }
      `}</style>
      <Container size={1500} mt="xl" className="vt-no-zoom">
        {error && (
          <Notification
            color="red"
            title="Verification Error"
            onClose={() => setError(null)}
            mb="lg"
          >
            {error}
          </Notification>
        )}

        <Paper withBorder p="lg" style={{ backgroundColor: "#fefefe" }}>
          <Group justify="space-between" p="xl" align="flex-start">
            <Stack gap="md" style={{ flex: 1, maxWidth: 600 }}>
              <Text fw={700} fz={30}>
                Demo Online Financial Services
              </Text>

              {!showForm && !verified && (
                <Button
                  onClick={() => setShowForm(true)}
                  color="grape"
                  mt="xl"
                  size="xl"
                >
                  Open an Account
                </Button>
              )}

              {verified && (
                <Paper p="md" radius="md" style={{ background: "linear-gradient(135deg, #d3f9d8 0%, #b2f2bb 100%)", border: "1px solid #69db7c" }}>
                  <Group>
                    <ThemeIcon size="lg" radius="xl" color="green">
                      <IconCheck size={20} />
                    </ThemeIcon>
                    <div>
                      <Text fw={600} c="green.8">Account Verified</Text>
                      <Text size="sm" c="green.7">Your identity has been verified. Welcome aboard!</Text>
                    </div>
                  </Group>
                </Paper>
              )}

              {showForm && !verified && (
                <Paper p="lg" withBorder mt="md">
                  <Group mb="md">
                    <ThemeIcon size="lg" radius="xl" color="gray">
                      <IconUser size={18} />
                    </ThemeIcon>
                    <div>
                      <Text fw={600}>Account Information</Text>
                      <Text size="sm" c="dimmed">Enter your details to open an account</Text>
                    </div>
                  </Group>

                  <Stack gap="sm">
                    <TextInput
                      label="Email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => handleFormChange("email", e.target.value)}
                      error={formErrors.email}
                      disabled={verified}
                      required
                    />
                    {(optionalFields.firstName || optionalFields.lastName) && (
                      <Group grow>
                        {optionalFields.firstName && (
                          <TextInput
                            label="First Name"
                            placeholder="John"
                            value={formData.firstName}
                            onChange={(e) => handleFormChange("firstName", e.target.value)}
                            error={formErrors.firstName}
                            disabled={verified}
                            required
                          />
                        )}
                        {optionalFields.lastName && (
                          <TextInput
                            label="Last Name"
                            placeholder="Doe"
                            value={formData.lastName}
                            onChange={(e) => handleFormChange("lastName", e.target.value)}
                            error={formErrors.lastName}
                            disabled={verified}
                            required
                          />
                        )}
                      </Group>
                    )}
                    {optionalFields.phone && (
                      <TextInput
                        label="Phone"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => handleFormChange("phone", e.target.value)}
                        error={formErrors.phone}
                        disabled={verified}
                        required
                      />
                    )}
                    {optionalFields.dob && (
                      <Group grow>
                        <Select
                          label="Month"
                          placeholder="Month"
                          data={[
                            { value: "1", label: "January" }, { value: "2", label: "February" },
                            { value: "3", label: "March" }, { value: "4", label: "April" },
                            { value: "5", label: "May" }, { value: "6", label: "June" },
                            { value: "7", label: "July" }, { value: "8", label: "August" },
                            { value: "9", label: "September" }, { value: "10", label: "October" },
                            { value: "11", label: "November" }, { value: "12", label: "December" },
                          ]}
                          value={formData.dobMonth}
                          onChange={(value) => handleFormChange("dobMonth", value)}
                          error={formErrors.dob}
                          disabled={verified}
                          required
                        />
                        <Select
                          label="Day"
                          placeholder="Day"
                          data={Array.from({ length: 31 }, (_, i) => ({
                            value: String(i + 1),
                            label: String(i + 1),
                          }))}
                          value={formData.dobDay}
                          onChange={(value) => handleFormChange("dobDay", value)}
                          error={formErrors.dob ? " " : ""}
                          disabled={verified}
                          required
                        />
                        <Select
                          label="Year"
                          placeholder="Year"
                          data={Array.from({ length: 100 }, (_, i) => {
                            const year = new Date().getFullYear() - 18 - i;
                            return { value: String(year), label: String(year) };
                          })}
                          value={formData.dobYear}
                          onChange={(value) => handleFormChange("dobYear", value)}
                          error={formErrors.dob ? " " : ""}
                          disabled={verified}
                          required
                          searchable
                        />
                      </Group>
                    )}
                    {optionalFields.ssn4 && (
                      <TextInput
                        label="Last 4 of SSN"
                        placeholder="1234"
                        value={formData.ssn4}
                        onChange={(e) => handleFormChange("ssn4", e.target.value.replace(/\D/g, "").slice(0, 4))}
                        maxLength={4}
                        error={formErrors.ssn4}
                        disabled={verified}
                        required
                      />
                    )}
                    {optionalFields.address && (
                      <>
                        <TextInput
                          label="Address"
                          placeholder="123 Main St"
                          value={formData.address}
                          onChange={(e) => handleFormChange("address", e.target.value)}
                          error={formErrors.address}
                          disabled={verified}
                          required
                        />
                        <Group grow>
                          <TextInput
                            label="City"
                            placeholder="New York"
                            value={formData.city}
                            onChange={(e) => handleFormChange("city", e.target.value)}
                            error={formErrors.city}
                            disabled={verified}
                            required
                          />
                          <Select
                            label="State"
                            placeholder="Select state"
                            data={US_STATES}
                            value={formData.state}
                            onChange={(value) => handleFormChange("state", value)}
                            error={formErrors.state}
                            disabled={verified}
                            required
                          />
                          <TextInput
                            label="ZIP Code"
                            placeholder="10001"
                            value={formData.zipCode}
                            onChange={(e) => handleFormChange("zipCode", e.target.value)}
                            error={formErrors.zipCode}
                            disabled={verified}
                            required
                          />
                        </Group>
                      </>
                    )}
                  </Stack>

                  {apiError && (
                    <Paper p="sm" mt="md" radius="md" bg="red.0">
                      <Group>
                        <ThemeIcon color="red" variant="light" size="sm">
                          <IconX size={14} />
                        </ThemeIcon>
                        <div>
                          <Text size="sm" fw={600} c="red">{apiError.error}</Text>
                          <Text size="sm" c="red">{apiError.message}</Text>
                        </div>
                      </Group>
                    </Paper>
                  )}

                  {verificationFailed && (
                    <Paper p="sm" mt="md" radius="md" bg="red.0">
                      <Group>
                        <ThemeIcon color="red" variant="light" size="sm">
                          <IconX size={14} />
                        </ThemeIcon>
                        <Text size="sm" c="red">
                          {failureCount >= 2
                            ? "Maximum attempts reached - Please contact support"
                            : "Verification failed - Please try again"}
                        </Text>
                      </Group>
                    </Paper>
                  )}

                  <Stack gap="xs" mt="lg">
                    <ConsentCheckbox brand="acme" acceptedAt={consentAcceptedAt} onChange={setConsentAcceptedAt} disabled={verified || loading} />
                  </Stack>

                  <Button
                    fullWidth
                    size="lg"
                    mt="md"
                    color="grape"
                    onClick={handleOpenAccount}
                    loading={loading}
                    disabled={!isFormComplete || failureCount >= 2 || consentRequired}
                    loaderProps={{ type: "dots" }}
                  >
                    {loading
                      ? loadingMessage
                      : failureCount >= 2
                        ? "Verification Unavailable"
                        : verificationFailed
                          ? "Try Again"
                          : "Verify & Open Account"}
                  </Button>
                  <Stack mt="xs"><ConsentIndicator /></Stack>
                </Paper>
              )}
            </Stack>
            <Box p="xl">
              <Image
                h={450}
                src={FinTechOne}
                alt={"fintech banner"}
              />
            </Box>
          </Group>
        </Paper>
      </Container>

      <VerificationIframe
        opened={verificationIframeOpened}
        onClose={() => setVerificationIframeOpened(false)}
        verificationLink={verificationLink}
        title="Identity Verification"
      />
    </>
  );
}
